import { useRef, useState, useEffect } from "react";
import { Attribution } from "ol/control.js";

import styles from "./Map.module.css";
import MapContext from "./MapContext";

import olMap from "ol/Map";
import olView from "ol/View";
import { Size as olSize } from "ol/size";

export interface MapProps {
  view?: olView;
  size?: olSize;
  children: React.ReactNode;
}

/**
 * Component to render the map wrapper and instantiate an OL Map within it.
 * Allows child components to be rendered inside, allowing further customisation
 * and to specify other map configuration such as layers or control in a markup fashion.
 * @component
 * @param {view} OpenLayers View for the map
 * @param {React.ReactNode} children - the children components to render
 * @example
 * <Map view={view}>
 *   <Layers>
 *     <Tile source={osmSource} />
 *   </Layers>
 *   <Controls>
 *     <FullScreen />
 *   </Controls>
 * </Map>
 * @remarks
 * Note, uses the MapContext to make the OL Map object available to child components.
 * Use useContext(MapContext) to obtain this OL Map object.
 */
const Map = ({ view, size, children }: MapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<olMap | null>(null);
  const attribution = new Attribution({
    collapsible: false,
  });
  useEffect(
    () => {
      const mapObject = new olMap({
        view: view,
        layers: [],
        controls: [attribution],
        overlays: [],
      });

      mapObject.setTarget(mapRef.current as HTMLDivElement);
      setMap(mapObject);

      return () => map?.setTarget(undefined);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!map || !view || !mapRef.current) return;
    map.setView(view);
  }, [view]);

  useEffect(() => {
    if (!map) return;
    map.setSize(size);
  }, [size]);

  return (
    <MapContext.Provider value={map}>
      <div ref={mapRef} className={styles.map}></div>
      {children}
    </MapContext.Provider>
  );
};

export default Map;
